import { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import { Badge, Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import { useListParams } from "hooks/useListParams"
import { useAccountLoans, useAccountLoan } from "hooks/useAccountLoan"
import { useAccountLoanRepayments, useAccountLoanRepayment } from "hooks/useAccountLoanRepayment"
import { useAccountLoanStatuses } from "hooks/useAccountLoanStatus"
import { useAccounts } from "hooks/useAccountEntity"
import { useUsers } from "hooks/userHook"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"
import AccountFilterPanel from "pages/AccountPages/utils/AccountFilterPanel"
import AccountLoanAddEdit from "pages/AccountPages/AccountLoan/AccountLoanAddEdit"
import AccountLoanView from "pages/AccountPages/AccountLoan/AccountLoanView"
import AccountLoanRepaymentAddEdit from "pages/AccountPages/AccountLoanRepayment/AccountLoanRepaymentAddEdit"
import AccountLoanRepaymentView from "pages/AccountPages/AccountLoanRepayment/AccountLoanRepaymentView"
import ReversalBadge from "pages/AccountPages/utils/ReversalBadge"
import { getGrossAmount, getReversedAmount, getReversedRecords, getNetAmount } from "pages/AccountPages/utils/reversalHelpers"
import WorkspaceStatCard from "../components/WorkspaceStatCard"

const LoansTab = ({ permissionFlags }) => {
  const [loanModal, setLoanModal] = useState(false)
  const [selectedLoan, setSelectedLoan] = useState(null)
  const [loanViewModal, setLoanViewModal] = useState(false)
  const [selectedLoanViewId, setSelectedLoanViewId] = useState(null)
  const [repaymentModal, setRepaymentModal] = useState(false)
  const [selectedRepayment, setSelectedRepayment] = useState(null)
  const [repaymentViewModal, setRepaymentViewModal] = useState(false)
  const [selectedRepaymentViewId, setSelectedRepaymentViewId] = useState(null)
  const [filters, setFilters] = useState({ date_from: "", date_to: "", borrower_id: "", account_id: "", loan_status_id: "" })
  const { params, setPage, setSearch } = useListParams({ page: 1, search: "" })

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleFilterReset = useCallback(() => {
    setFilters({ date_from: "", date_to: "", borrower_id: "", account_id: "", loan_status_id: "" })
  }, [])

  const queryParams = useMemo(() => ({
    page: params.page || 1,
    per_page: 6,
    search: params.search || "",
    ...(filters.date_from && { date_from: filters.date_from }),
    ...(filters.date_to && { date_to: filters.date_to }),
    ...(filters.borrower_id && { borrower_id: filters.borrower_id }),
    ...(filters.account_id && { account_id: filters.account_id }),
    ...(filters.loan_status_id && { loan_status_id: filters.loan_status_id }),
  }), [params, filters])

  const repaymentParams = useMemo(() => ({
    page: 1,
    per_page: 6,
    search: params.search || "",
    ...(filters.date_from && { date_from: filters.date_from }),
    ...(filters.date_to && { date_to: filters.date_to }),
    ...(filters.account_id && { account_id: filters.account_id }),
    ...(selectedLoanViewId && { loan_id: selectedLoanViewId }),
  }), [params.search, filters, selectedLoanViewId])

  const { data: loanResponse, isLoading: loanLoading } = useAccountLoans(queryParams, permissionFlags.canViewLoans)
  const { data: repaymentResponse, isLoading: repaymentLoading } = useAccountLoanRepayments(repaymentParams, permissionFlags.canViewLoanRepayments)
  const { data: loanStatusResponse } = useAccountLoanStatuses({ all: true, per_page: 200 }, permissionFlags.canViewLoans)
  const { data: accountResponse } = useAccounts({ all: true, per_page: 200 }, permissionFlags.canViewLoans)
  const { data: borrowerResponse } = useUsers({ all: true, per_page: 200 }, permissionFlags.canViewLoans)
  const { data: loanViewResponse, isLoading: loanViewLoading } = useAccountLoan(selectedLoanViewId, loanViewModal)
  const { data: repaymentViewResponse, isLoading: repaymentViewLoading } = useAccountLoanRepayment(selectedRepaymentViewId, repaymentViewModal)

  const loans = loanResponse?.data?.data || []
  const repayments = repaymentResponse?.data?.data || []
  const reversedRepayments = useMemo(() => getReversedRecords(repayments), [repayments])
  const grossRepaymentAmount = useMemo(() => getGrossAmount(repayments), [repayments])
  const reversedRepaymentAmount = useMemo(() => getReversedAmount(repayments), [repayments])
  const statuses = loanStatusResponse?.data?.data || loanStatusResponse?.data || []
  const accounts = accountResponse?.data?.data || accountResponse?.data || []
  const borrowers = borrowerResponse?.data?.data || borrowerResponse?.data || []

  const summary = useMemo(() => {
    const outstanding = loans.reduce((sum, item) => sum + Number(item.remaining || 0), 0)
    const overdueCount = loans.filter(item => (item.loan_status?.slug || item.loanStatus?.slug || "") === "overdue").length
    return {
      loanCount: loanResponse?.data?.total ?? loans.length,
      grossRepaymentAmount,
      reversedRepaymentAmount,
      repaymentAmount: getNetAmount(repayments),
      reversedRepaymentCount: reversedRepayments.length,
      outstanding,
      overdueCount,
    }
  }, [loans, repayments, loanResponse, reversedRepayments, grossRepaymentAmount, reversedRepaymentAmount])

  const loanColumns = useMemo(() => [
    { header: "Borrower", cell: ({ row }) => row.original.borrower?.name || "-" },
    { header: "Account", cell: ({ row }) => row.original.account?.name || "-" },
    { header: "Loan Date", cell: ({ row }) => row.original.loan_date ? formatReadableDate(row.original.loan_date) : "-" },
    {
      header: "Amount",
      cell: ({ row }) => (
        <div className="d-flex gap-1 align-items-center">
          <span className={`fw-semibold ${row.original.is_reversed ? "text-muted" : ""}`}>{formatCurrency(row.original.amount || 0, row.original.account?.currency || "BDT")}</span>
          <ReversalBadge isReversed={row.original.is_reversed} />
        </div>
      ),
    },
    { header: "Remaining", cell: ({ row }) => <Badge color={Number(row.original.remaining || 0) === 0 ? "success" : "warning"} className="text-dark">{formatCurrency(row.original.remaining || 0, row.original.account?.currency || "BDT")}</Badge> },
    {
      header: "Action",
      cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button size="sm" color="primary" onClick={() => { setSelectedLoanViewId(row.original.id); setLoanViewModal(true) }}>View</Button>
          {permissionFlags.canAddRepayment && <Button size="sm" color="light" className="border" onClick={() => { setSelectedRepayment({ loan_id: row.original.id, loan: row.original }); setRepaymentModal(true) }}>Repay</Button>}
        </div>
      ),
    },
  ], [permissionFlags.canAddRepayment])

  const repaymentColumns = useMemo(() => [
    { header: "Borrower", cell: ({ row }) => row.original.loan?.borrower?.name || "-" },
    { header: "Date", cell: ({ row }) => row.original.repayment_date ? formatReadableDate(row.original.repayment_date) : "-" },
    { header: "Account", cell: ({ row }) => row.original.account?.name || "-" },
    { header: "Method", cell: ({ row }) => row.original.payment_method?.name || row.original.paymentMethod?.name || "-" },
    {
      header: "Amount",
      cell: ({ row }) => (
        <div className="d-flex gap-1 align-items-center">
          <span className={`fw-semibold ${row.original.is_reversed ? "text-muted" : ""}`}>{formatCurrency(row.original.amount || 0, row.original.account?.currency || "BDT")}</span>
          <ReversalBadge isReversed={row.original.is_reversed} />
        </div>
      ),
    },
    { header: "Action", cell: ({ row }) => <Button size="sm" color="primary" onClick={() => { setSelectedRepaymentViewId(row.original.id); setRepaymentViewModal(true) }}>View</Button> },
  ], [])

  return (
    <div className="d-flex flex-column gap-4">
      <Row className="g-3">
        {permissionFlags.canViewLoans && <Col md={6} xl={3}><WorkspaceStatCard title="Visible Loans" value={summary.loanCount} subtitle="Loans matching the current queue" icon="mdi mdi-cash" color="primary" /></Col>}
        {permissionFlags.canViewLoanRepayments && <Col md={6} xl={3}><WorkspaceStatCard title="Gross Repayments" value={formatCurrency(summary.grossRepaymentAmount, "BDT")} subtitle="All visible repayment records" icon="mdi mdi-cash-multiple" color="secondary" /></Col>}
        {permissionFlags.canViewLoanRepayments && <Col md={6} xl={3}><WorkspaceStatCard title="Reversed Repayments" value={formatCurrency(summary.reversedRepaymentAmount, "BDT")} subtitle={`${summary.reversedRepaymentCount} reversed repayment(s)`} icon="mdi mdi-undo-variant" color="info" /></Col>}
        {permissionFlags.canViewLoanRepayments && <Col md={6} xl={3}><WorkspaceStatCard title="Net Repayments" value={formatCurrency(summary.repaymentAmount, "BDT")} subtitle="Recovered value after reversals" icon="mdi mdi-cash-sync" color="success" /></Col>}
        {permissionFlags.canViewLoans && <Col md={6} xl={3}><WorkspaceStatCard title="Outstanding" value={formatCurrency(summary.outstanding, "BDT")} subtitle="Remaining balance after valid repayments" icon="mdi mdi-finance" color="warning" /></Col>}
        {permissionFlags.canViewLoans && <Col md={6} xl={3}><WorkspaceStatCard title="Overdue Loans" value={summary.overdueCount} subtitle="Loans needing follow-up" icon="mdi mdi-alert" color="danger" /></Col>}
      </Row>

      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
            <div>
              <h5 className="mb-1">Loans Workspace</h5>
              <p className="text-muted mb-0 small">Issue loans, review balances, and post repayments without leaving the same tab.</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {permissionFlags.canAddLoan && <Button color="primary" onClick={() => { setSelectedLoan(null); setLoanModal(true) }}><i className="mdi mdi-plus me-1" />Add Loan</Button>}
              {permissionFlags.canAddRepayment && <Button color="success" onClick={() => { setSelectedRepayment(null); setRepaymentModal(true) }}><i className="mdi mdi-cash-plus me-1" />Add Repayment</Button>}
              <Link to="/account-loan-list" className="btn btn-light border">Open full loans page</Link>
            </div>
          </div>

          <AccountFilterPanel filters={filters} onChange={handleFilterChange} onReset={handleFilterReset}>
            <Col xs={12} sm={6} md={3}>
              <Label className="mb-1 small fw-semibold">Borrower</Label>
              <Input type="select" bsSize="sm" value={filters.borrower_id || ""} onChange={e => handleFilterChange("borrower_id", e.target.value)}>
                <option value="">All Borrowers</option>
                {borrowers.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              </Input>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Label className="mb-1 small fw-semibold">Account</Label>
              <Input type="select" bsSize="sm" value={filters.account_id || ""} onChange={e => handleFilterChange("account_id", e.target.value)}>
                <option value="">All Accounts</option>
                {accounts.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              </Input>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Label className="mb-1 small fw-semibold">Status</Label>
              <Input type="select" bsSize="sm" value={filters.loan_status_id || ""} onChange={e => handleFilterChange("loan_status_id", e.target.value)}>
                <option value="">All Statuses</option>
                {statuses.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              </Input>
            </Col>
          </AccountFilterPanel>

          <Row className="mb-3">
            <Col md={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
          </Row>

          <Row className="g-3">
            {permissionFlags.canViewLoans && (
              <Col xl={7}>
                <Card className="border h-100 mb-0">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Loans</h6>
                      <Link to="/account-loan-list" className="small">Open full page</Link>
                    </div>
                    <TableContainer columns={loanColumns} data={loans} loading={loanLoading} isPagination={true} paginationInfo={loanResponse?.data} onPageChange={setPage} />
                  </CardBody>
                </Card>
              </Col>
            )}
            {permissionFlags.canViewLoanRepayments && (
              <Col xl={permissionFlags.canViewLoans ? 5 : 12}>
                <Card className="border h-100 mb-0">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h6 className="mb-0">Repayments</h6>
                        <div className="text-muted small">{selectedLoanViewId ? "Filtered by the selected loan" : "Latest repayments"}</div>
                      </div>
                      <Link to="/account-loan-repayment-list" className="small">Open full page</Link>
                    </div>
                    <TableContainer columns={repaymentColumns} data={repayments} loading={repaymentLoading} isPagination={false} />
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </CardBody>
      </Card>

      <AccountLoanAddEdit modal={loanModal} isEdit={!!selectedLoan?.id} editedData={selectedLoan} toggleModal={() => { setLoanModal(prev => !prev); if (loanModal) setSelectedLoan(null) }} />
      <AccountLoanView modal={loanViewModal} toggleModal={() => { setLoanViewModal(prev => !prev); if (loanViewModal) setSelectedLoanViewId(null) }} loan={loanViewResponse?.data} loading={loanViewLoading} canAddRepayment={permissionFlags.canAddRepayment} onAddRepayment={loan => { setSelectedRepayment({ loan_id: loan?.id, loan }); setRepaymentModal(true) }} />
      <AccountLoanRepaymentAddEdit modal={repaymentModal} isEdit={false} editedData={selectedRepayment} toggleModal={() => { setRepaymentModal(prev => !prev); if (repaymentModal) setSelectedRepayment(null) }} />
      <AccountLoanRepaymentView modal={repaymentViewModal} toggleModal={() => { setRepaymentViewModal(prev => !prev); if (repaymentViewModal) setSelectedRepaymentViewId(null) }} repayment={repaymentViewResponse?.data} loading={repaymentViewLoading} />
    </div>
  )
}

export default LoansTab
